import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IProduct } from 'src/app/models/products/product';
import { FormatType, ProductValidation } from './product-validation-list.component';
import { ProductUpdateComponent } from '../product-update/product-update.component';

export type TokenType = 'barcode' | 'reference' | 'description' | 'index' | 'alphabetical-index' | 'extension' | 'literal';

export interface PatternToken {
  text: string;
  type: TokenType;
}

export interface FilenameToken extends PatternToken {
  valid: boolean;
}

export interface ProductValidationDialogData {
  validations: ProductValidation[];
  totalProducts: number;
  expectedAssets: number;
  organizationId?: string;
  validateProductDataExport: boolean;
}

export interface PreviewWithTokens {
  pattern: string;
  patternTokens: PatternToken[];
  filenameTokens: FilenameToken[];
  valid: boolean;
  extension?: string;
  type?: FormatType;
  formatName?: string;
  from?: number;
  to?: number | null;
  index?: number;
  indexOffset?: number;
}

export interface ValidationWithTokens {
  product: IProduct;
  valid: boolean;
  previews: PreviewWithTokens[];
}

const TOKEN_REGEX = /\{(barcode|reference|description|index|alphabetical-index|extension)\}/g;

const TOKEN_COLOR_CLASSES: Record<string, string> = {
  barcode: 'token-barcode',
  reference: 'token-reference',
  description: 'token-description',
  index: 'token-index',
  'alphabetical-index': 'token-alphabetical-index',
  extension: 'token-extension',
  literal: 'token-literal',
};

@Component({
  selector: 'app-product-validation-dialog',
  templateUrl: './product-validation-dialog.component.html',
  styleUrls: ['./product-validation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ProductValidationDialogComponent {
  validationsWithTokens: ValidationWithTokens[] = [];
  groupedRows: { product: IProduct; formatName: string; stills: PreviewWithTokens[]; videos: PreviewWithTokens[]; json: PreviewWithTokens[]; valid: boolean; productGroup: number }[] = [];

  constructor(
    public dialogRef: MatDialogRef<ProductValidationDialogComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ProductValidationDialogData,
  ) {
    this.validationsWithTokens = this.precomputeTokens();
    this.groupedRows = this.computeGroupedRows();
  }

  private revalidate(): void {
    this.validationsWithTokens = this.validationsWithTokens.map((validation) => {
      const previews: PreviewWithTokens[] = validation.previews.map((preview) => {
        const extension = preview.extension || 'jpg';
        const index = preview.index ?? 1;
        const indexOffset = preview.indexOffset ?? 0;
        const substituted = this.substitutePattern(preview.pattern, validation.product, extension, index, indexOffset);
        const valid = /^[a-zA-Z0-9]/.test(substituted);

        return {
          ...preview,
          patternTokens: this.tokenizePattern(preview.pattern),
          filenameTokens: this.tokenizeFilename(preview.pattern, substituted, validation.product, extension, index, indexOffset),
          valid,
        };
      });

      const allValid = previews.every((p) => p.valid);

      return {
        ...validation,
        valid: allValid,
        previews,
      };
    });
    this.groupedRows = this.computeGroupedRows();
  }

  private substitutePattern(pattern: string, product: IProduct, extension: string = 'jpg', index: number = 1, indexOffset: number = 0): string {
    const barcode = (product.barcode || '').replace(/ /g, '_');
    const reference = (product.reference || '').replace(/ /g, '_');
    const description = (product.description || '').replace(/ /g, '_');

    return pattern
      .replace(/\{barcode\}/g, barcode)
      .replace(/\{reference\}/g, reference)
      .replace(/\{description\}/g, description)
      .replace(/\{index\}/g, (index + indexOffset).toString())
      .replace(/\{alphabetical-index\}/g, String.fromCharCode(index + 96 + indexOffset))
      .replace(/\{extension\}/g, extension);
  }

  private precomputeTokens(): ValidationWithTokens[] {
    return this.data.validations.map((validation) => ({
      product: validation.product,
      valid: validation.valid,
      previews: validation.previews.map((preview) => {
        const extension = preview.extension || 'jpg';
        return {
          pattern: preview.pattern,
          patternTokens: this.tokenizePattern(preview.pattern),
          filenameTokens: this.tokenizeFilename(preview.pattern, preview.substituted, validation.product, extension, preview.index ?? 1, preview.indexOffset ?? 0),
          valid: preview.valid,
          extension,
          type: preview.type,
          formatName: preview.formatName,
          from: preview.from,
          to: preview.to,
          index: preview.index,
          indexOffset: preview.indexOffset,
        };
      }),
    }));
  }

  get validProductsCount(): number {
    return this.validationsWithTokens.filter((v) => v.valid).length;
  }

  get invalidProductsCount(): number {
    return this.validationsWithTokens.filter((v) => !v.valid).length;
  }

  /**
   * Groups validation previews into table rows: one row per product + output format combination.
   *
   * Structure: flatMap over products, where each product's previews are grouped by formatName.
   * - Outer flatMap: iterates products, returns multiple rows per product (one per format)
   * - Inner reduce: groups previews by formatName into {stills, videos, json} using immutable Map
   * - Final map: converts Map entries to row objects with validity check
   *
   * Immutability: reduce creates new Map on each iteration via `new Map(acc).set(key, updated)`
   */
  private computeGroupedRows(): { product: IProduct; formatName: string; stills: PreviewWithTokens[]; videos: PreviewWithTokens[]; json: PreviewWithTokens[]; valid: boolean; productGroup: number }[] {
    return this.validationsWithTokens.flatMap((validation, productIndex) => {
      const formatGroups = validation.previews.reduce(
        (acc, preview) => {
          const key = preview.formatName || 'Product Data';
          const existing = acc.get(key) || { stills: [], videos: [], json: [] };
          const updated = {
            stills: preview.type === 'stills' ? [...existing.stills, preview] : existing.stills,
            videos: preview.type === 'videos' ? [...existing.videos, preview] : existing.videos,
            json: preview.type === 'json' ? [...existing.json, preview] : existing.json,
          };
          return new Map(acc).set(key, updated);
        },
        new Map<string, { stills: PreviewWithTokens[]; videos: PreviewWithTokens[]; json: PreviewWithTokens[] }>()
      );

      return Array.from(formatGroups.entries()).map(([formatName, group]) => {
        const allPreviews = [...group.stills, ...group.videos, ...group.json];
        const valid = allPreviews.every((p) => p.valid);
        return { product: validation.product, formatName, ...group, valid, productGroup: productIndex };
      });
    });
  }

  get validFilenamesCount(): number {
    return this.validationsWithTokens.reduce(
      (total, v) => total + v.previews.filter((p) => p.valid).length,
      0
    );
  }

  get invalidFilenamesCount(): number {
    return this.validationsWithTokens.reduce(
      (total, v) => total + v.previews.filter((p) => !p.valid).length,
      0
    );
  }

  get stillsCount(): number {
    return this.validationsWithTokens.reduce(
      (total, v) => total + v.previews.filter((p) => p.type === 'stills').length,
      0
    );
  }

  get videosCount(): number {
    return this.validationsWithTokens.reduce(
      (total, v) => total + v.previews.filter((p) => p.type === 'videos').length,
      0
    );
  }

  get jsonFilesCount(): number {
    return this.data.validateProductDataExport ? this.data.totalProducts : 0;
  }

  get totalFilesCount(): number {
    return this.stillsCount + this.videosCount + this.jsonFilesCount;
  }

  get labelWarningsCount(): number {
    return this.data.validateProductDataExport
      ? this.data.validations.filter((v) => v.previews.some((p) => p.type === 'json' && !p.valid)).length
      : 0;
  }

  get duplicateBarcodeCount(): number {
    if (!this.data.validateProductDataExport) {
      return 0;
    }
    const barcodeCounts = this.data.validations.reduce(
      (acc, v) => {
        const bc = (v.product.barcode ?? '').trim();
        return bc ? new Map(acc).set(bc, (acc.get(bc) ?? 0) + 1) : acc;
      },
      new Map<string, number>()
    );
    return Array.from(barcodeCounts.values()).filter((count) => count > 1).length;
  }

  /**
   * Splits a pattern string into typed tokens for color-coded display.
   * Example: "{barcode}_photo.{extension}" -> [{barcode}, {literal:"_photo."}, {extension}]
   *
   * Uses reduce to walk through regex matches, capturing literal text between tokens.
   * Accumulator tracks: tokens built so far, lastIndex position in pattern string.
   * After reduce, appends any trailing literal text after the last token.
   */
  tokenizePattern(pattern: string): PatternToken[] {
    const matches = Array.from(pattern.matchAll(TOKEN_REGEX)).map((match) => ({
      match: match[0],
      tokenType: match[1],
      offset: match.index!,
    }));

    const { tokens } = matches.reduce(
      (acc, { match, tokenType, offset }) => {
        const literalBefore = pattern.slice(acc.lastIndex, offset);
        const newTokens = literalBefore
          ? [...acc.tokens, { text: literalBefore, type: 'literal' as TokenType }, { text: match, type: tokenType as TokenType }]
          : [...acc.tokens, { text: match, type: tokenType as TokenType }];
        return { tokens: newTokens, lastIndex: offset + match.length };
      },
      { tokens: [] as PatternToken[], lastIndex: 0 }
    );

    const literalAfter = pattern.slice(matches.length > 0 ? matches[matches.length - 1].offset + matches[matches.length - 1].match.length : 0);
    return literalAfter ? [...tokens, { text: literalAfter, type: 'literal' as TokenType }] : tokens;
  }

  /**
   * Maps pattern tokens to their substituted values in the filename, with validation.
   * Example: pattern "{barcode}.jpg" + product.barcode="SKU123" -> ["SKU123", ".", "jpg"] with validity flags
   *
   * Uses reduce to slice through the substituted string based on each token's expected length.
   * Accumulator tracks: tokens built so far, current position in substituted string.
   * Each token's length is determined by the actual product field value (or literal text length).
   */
  tokenizeFilename(pattern: string, substituted: string, product: IProduct, extension: string = 'jpg', index: number = 1, indexOffset: number = 0): FilenameToken[] {
    const patternTokens = this.tokenizePattern(pattern);

    const { tokens } = patternTokens.reduce(
      (acc, patternToken) => {
        const length = patternToken.type === 'literal'
          ? patternToken.text.length
          : this.getTokenValue(patternToken.type, product, extension, index, indexOffset).length;
        const text = substituted.slice(acc.position, acc.position + length);
        const valid = this.isTokenValid(patternToken.type, text, acc.position === 0);

        return {
          tokens: [...acc.tokens, { text, type: patternToken.type, valid }],
          position: acc.position + length,
        };
      },
      { tokens: [] as FilenameToken[], position: 0 }
    );

    return tokens;
  }

  getTokenColorClass(type: string): string {
    return TOKEN_COLOR_CLASSES[type] || 'token-literal';
  }

  openEditProduct(product: IProduct): void {
    const dialogRef = this.dialog.open(ProductUpdateComponent, {
      data: product,
    });

    dialogRef.afterClosed().subscribe((updatedProduct: IProduct | undefined) => {
      if (updatedProduct) {
        // Update the product in our data
        const validation = this.data.validations.find((v) => v.product.id === product.id);
        if (validation) {
          Object.assign(validation.product, updatedProduct);
        }
        this.revalidate();
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private getTokenValue(type: TokenType, product: IProduct, extension: string = 'jpg', index: number = 1, indexOffset: number = 0): string {
    const valueMap: Record<TokenType, string> = {
      barcode: (product.barcode || '').replace(/ /g, '_'),
      reference: (product.reference || '').replace(/ /g, '_'),
      description: (product.description || '').replace(/ /g, '_'),
      index: (index + indexOffset).toString(),
      'alphabetical-index': String.fromCharCode(index + 96 + indexOffset),
      extension,
      literal: '',
    };
    return valueMap[type];
  }

  private isTokenValid(type: TokenType, text: string, isFirstToken: boolean): boolean {
    const productFields: TokenType[] = ['barcode', 'reference', 'description'];
    if (isFirstToken && productFields.includes(type)) {
      return /^[a-zA-Z0-9]/.test(text);
    }
    return true;
  }
}
